import { AppUser } from "@eventr/common";
import { Avatar } from "@mui/material";
import { IRCUser } from "data/Rocket";
import { FC } from "react";
import { useBoundStore } from "state/bound.store";
import { previewOnClick } from "state/overlayState/nativeImagePreview";

type props = {
  user?: AppUser;
  sideLength?: number;
  rcUser?: IRCUser;
  /** Tap to preview full-size. Off by default - this component is also used for small avatars. */
  previewable?: boolean;
  /** The stored size to draw; by default tiny up to 64px across and medium above. */
  pictureSize?: AvatarPictureSize;
};

/** The stored sizes of a member's picture: 150px, about 300px, and the original upload. */
export type AvatarPictureSize = "tiny" | "medium" | "full";

/** Exported so the web avatar and the native title view derive the same colour for a member. */
export function avatarColorFor(string: string) {
  let hash = 0;
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = "#";

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
}

export function avatarInitialsFor(user?: AppUser, rcUser?: IRCUser): string | undefined {
  if ((user && user.firstName && user.lastName) || (rcUser && rcUser.name)) {
    if (user) {
      return [user?.firstName[0]?.toUpperCase() ?? "", user?.lastName[0]?.toUpperCase() ?? ""].join("");
    } else if (rcUser) {
      const userFromName = useBoundStore.getState().getUserFromName(rcUser.name);
      if (userFromName) {
        return [userFromName?.firstName[0]?.toUpperCase() ?? "", userFromName?.lastName[0]?.toUpperCase() ?? ""].join(
          "",
        );
      }
    }
    /// Below has not been fixed but should not be used. will split the name into many parts not just 2 initials.
    if (rcUser) {
      return rcUser?.name
        .split(" ")
        .map((e) => e[0]?.toUpperCase() ?? "")
        .join("");
    }
  }
  return undefined;
}

/** The member's picture at `size`, or the nearest size stored. */
export function avatarPictureUrlFor(
  user?: AppUser,
  rcUser?: IRCUser,
  size: AvatarPictureSize = "tiny",
): string | undefined {
  const sizes = user?.profilePicSizes;
  const bySize = {
    tiny: [sizes?.tiny?.url, sizes?.medium?.url, user?.profilePicUrl],
    medium: [sizes?.medium?.url, user?.profilePicUrl, sizes?.tiny?.url],
    full: [user?.profilePicUrl, sizes?.medium?.url, sizes?.tiny?.url],
  }[size];
  return bySize.find(Boolean) ?? rcUser?.customFields?.avatarUrl ?? undefined;
}

export const MemberProfilePic: FC<props> = ({ user, sideLength, rcUser, previewable, pictureSize }) => {
  const color = avatarColorFor(user?.email ?? rcUser?.username ?? "");
  const drawInitials = () => !!avatarInitialsFor(user, rcUser);
  const getInitials = () => avatarInitialsFor(user, rcUser) ?? "";
  const size = pictureSize ?? ((sideLength ?? 32) > 64 ? "medium" : "tiny");
  const pictureUrl = avatarPictureUrlFor(user, rcUser, size);
  const onPreviewClick = previewOnClick(avatarPictureUrlFor(user, rcUser, "full"), !!previewable);
  return (
    <div
      onClick={onPreviewClick}
      style={{
        backgroundColor: `${color}30`,
        borderRadius: "50%",
        cursor: onPreviewClick ? "pointer" : undefined,
      }}
    >
      <Avatar
        sx={{
          height: `${sideLength ? sideLength : 32}px`,
          width: `${sideLength ? sideLength : 32}px`,
          fontSize: (sideLength ?? 0) > 40 ? "150%" : "14px",
          fontFamily: "Inter-100-500",
          fontWeight: 500,
          color: color,
          bgcolor: `transparent`,
          filter: "brightness(80%)",
          transition: "0.5s",
        }}
        src={pictureUrl}
      >
        {drawInitials() ? (
          getInitials()
        ) : (
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clipPath="url(#clip0_4067_54)">
              <rect width="32" height="32" rx="16" fill={color} fillOpacity="0.2" />
              <path
                d="M5.83131 23.6584C6.45669 20.9324 8.88257 19 11.6794 19H20.3206C23.1174 19 25.5433 20.9324 26.1687 23.6584L27.3157 28.6584C28.1778 32.4161 25.323 36 21.4677 36H10.5323C6.677 36 3.82219 32.4161 4.68425 28.6584L5.83131 23.6584Z"
                fill={color}
              />
              <circle cx="16" cy="12" r="6" fill={color} />
            </g>
            <defs>
              <clipPath id="clip0_4067_54">
                <rect width="32" height="32" rx="16" fill={color} />
              </clipPath>
            </defs>
          </svg>
        )}
      </Avatar>
    </div>
  );
};
